import dayjs from "dayjs";
import numeral from "numeral";

import React, { useCallback, useState } from "react";

import { Box, Grid, Tooltip, Typography } from "@mui/material";

import usePlatform from "@/hooks/usePlatform";
import { CalendarItemType } from "@/typings";

interface CalendarItemProps {
  data: CalendarItemType;
  month: string;
  onItemClick?: (data: CalendarItemType["points"][number]) => void;
}

const PointItem = ({
  data,
  color,
  onItemClick,
}: {
  data: CalendarItemType["points"][number];
  color?: string;
  onItemClick?: (data: CalendarItemType["points"][number]) => void;
}) => {
  const { isMobile, isPc } = usePlatform();
  const available = !!data.value;

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const formatVal = useCallback(
    (val: number, unit = "") => {
      if (val >= 1000 && isMobile) {
        return `${numeral(val / 1000).format("0,0")}k ${isMobile ? "" : unit}`;
      }

      return `${numeral(val).format("0,0")} ${isMobile ? "" : unit}`;
    },
    [isMobile]
  );

  return (
    <Tooltip
      open={isPc && open && !!data.value}
      title={
        <div>
          <Typography className="inline" fontSize={14}>
            {data.sourceData?.cash_price
              ? `$${Number(data.sourceData?.cash_price).toLocaleString()}`
              : "-"}
            {" · "}
            {data.sourceData?.points_rate ? `${data.sourceData?.points_rate / 1000}K` : "-"}
            {" · "}
            {data.sourceData?.point_value ? `${data.sourceData?.point_value}¢/pt` : "-"}
            {" · "}
            {data.sourceData?.rate_plan ? `${data.sourceData?.rate_plan} Room` : ""}
          </Typography>
        </div>
      }
      placement="top"
      disableHoverListener
      disableFocusListener
      disableTouchListener
      onClose={handleClose}
    >
      <Box
        className="mb-[2px] rounded-[3px] cursor-pointer"
        sx={{
          backgroundColor: color || "transparent",
          opacity: available ? 1 : 0.38,
        }}
        onClick={() => (available && onItemClick ? onItemClick(data) : undefined)}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
      >
        <Typography
          className="px-[2px] h-[24px] !text-[#fff] !leading-[24px] overflow-hidden text-ellipsis whitespace-nowrap"
          sx={{
            fontSize: isMobile ? "12px" : "13px",
            fontWeight: available ? "normal" : "bold",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          // title={available ? formatVal(data.value as number, data.unit) : ""}
        >
          {data.icon}
          {available ? data.value : ""}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const CalendarItem = (props: CalendarItemProps) => {
  const {
    data: { date, showDate = true, points },
    month,
    onItemClick,
  } = props;

  const isToday = date === dayjs().format("YYYY-MM-DD");
  const isCurrentMonth = dayjs(date).format("YYYY-MM") === month;

  return (
    <Grid key={date} item xs={12 / 7} className="pb-[20px]">
      <Box
        className="flex flex-col px-[2px] h-full"
        sx={{
          backgroundColor: (theme) => (isToday ? theme.palette.action.selected : ""),
          borderRadius: "4px",
        }}
      >
        <Typography
          className="text-right !text-[15px] p-[2px]"
          sx={{
            color: (theme) =>
              isCurrentMonth ? theme.palette.text.primary : theme.palette.text.disabled,
          }}
        >
          {dayjs(date).format("D")}
        </Typography>

        {(points || []).map((ele, index) => {
          return showDate ? (
            <PointItem data={ele} key={index} color={ele.color} onItemClick={onItemClick} />
          ) : (
            <Box
              key={index}
              className="mb-[2px] rounded-[3px] px-[2px] h-[24px]"
              sx={{ backgroundColor: ele.color || "transparent", opacity: 0.38 }}
            />
          );
        })}
      </Box>
    </Grid>
  );
};

export default React.memo(CalendarItem);
